import { CircleInfoIcon } from "@xola/icons";
import { InlineValuePopover, PopoverList, Tooltip } from "@xola/ui-kit";
import { SwitchField } from "common";
import { useFormikContext } from "formik";
import React from "react";
import { ExperienceNotification, NotificationPreferenceType } from "types";
import { GuestManagementFormValues } from "./GuestManagement";
import { useVisibility } from "@/hooks/useVisibility";

export const DEFAULT_NOTIFICATIONS: ExperienceNotification[] = [
    { type: NotificationPreferenceType.ORDER_CHANGED_TRAVELER, recipients: "organizer" },
    { type: NotificationPreferenceType.ORDER_CANCELED_TRAVELER, recipients: "organizer" },
    { type: NotificationPreferenceType.INVOICE_CHANGED_TRAVELER, recipients: "organizer" },
    { type: NotificationPreferenceType.TRIP_REMINDER_TRAVELER, recipients: "travelers" },
    { type: NotificationPreferenceType.REVIEW_REMINDER_TRAVELER, recipients: "organizer" },
    { type: NotificationPreferenceType.WAIVER_REMINDER_TRAVELER, recipients: "organizer" },
];

const NOTIFICATION_CONFIG = [
    {
        type: NotificationPreferenceType.ORDER_CHANGED_TRAVELER,
        label: "Purchase Modification",
        description: "Sent when a purchase is modified to",
    },
    {
        type: NotificationPreferenceType.ORDER_CANCELED_TRAVELER,
        label: "Purchase Canceled",
        description: "Sent when a purchase is canceled to",
    },
    {
        type: NotificationPreferenceType.INVOICE_CHANGED_TRAVELER,
        label: "Purchase Modifications for Completed Events",
        description: "Sent when a purchase is modified for an event in the past to",
    },
    {
        type: NotificationPreferenceType.TRIP_REMINDER_TRAVELER,
        label: "Event Reminder",
        description: "Sent a day prior to the start of event to",
    },
    {
        type: NotificationPreferenceType.REVIEW_REMINDER_TRAVELER,
        label: "Review Reminder",
        description: "Sent after the end of the event to",
    },
    {
        type: NotificationPreferenceType.WAIVER_REMINDER_TRAVELER,
        label: "Waiver Reminder",
        description: "Sent before event date to",
    },
];

const RECIPIENTS_COPY: Record<string, string> = {
    organizer: "organizer only",
    travelers: "all attendees",
};

/** Merges experience notifications with defaults, backfilling any missing notification types */
export const getNotificationsWithDefaults = (notifications?: ExperienceNotification[]): ExperienceNotification[] => {
    if (!notifications?.length) return [...DEFAULT_NOTIFICATIONS];

    return DEFAULT_NOTIFICATIONS.map((defaultNotification) => {
        const existing = notifications.find((n) => n.type === defaultNotification.type);
        return existing ?? defaultNotification;
    });
};

export const isCustomized = (notifications?: ExperienceNotification[]) => {
    if (!notifications || notifications.length === 0) return false;

    return notifications.some((notification) => {
        const defaultEntry = DEFAULT_NOTIFICATIONS.find((d) => d.type === notification.type);
        return defaultEntry && defaultEntry.recipients !== notification.recipients;
    });
};

export const NotificationsSection = () => {
    const { values, setFieldValue } = useFormikContext<GuestManagementFormValues>();

    const handleCustomizeToggle = (value: boolean) => {
        setFieldValue("isNotificationsCustomized", value);
        if (!value) {
            setFieldValue("notifications", DEFAULT_NOTIFICATIONS);
        }
    };

    const handleRecipientChange = (
        type: NotificationPreferenceType,
        recipients: ExperienceNotification["recipients"],
    ) => {
        const updated = values.notifications.map((n) => (n.type === type ? { ...n, recipients } : n));
        setFieldValue("notifications", updated);
    };

    if (!values.requireGuestIdentification) return null;

    return (
        <div className="mt-6 space-y-4">
            <h4 className="flex items-center space-x-1 text-sm font-bold text-gray-darker">
                <span>Notifications</span>
                <Tooltip
                    content={
                        <>
                            Select whether to notify only
                            <br />
                            the organizer or all attendees.
                        </>
                    }
                    placement="left"
                >
                    <CircleInfoIcon className="h-4 w-4 text-gray" />
                </Tooltip>
            </h4>
            <SwitchField isChecked={values.isNotificationsCustomized} onChange={handleCustomizeToggle}>
                Customize recipients for reservation related notifications
            </SwitchField>

            {values.isNotificationsCustomized && (
                <div className="space-y-4">
                    {NOTIFICATION_CONFIG.map((config) => (
                        <NotificationRow
                            key={config.type}
                            config={config}
                            recipients={
                                values.notifications.find((n) => n.type === config.type)?.recipients ?? "organizer"
                            }
                            onChange={(recipients) => handleRecipientChange(config.type, recipients)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface NotificationRowProps {
    config: { type: NotificationPreferenceType; label: string; description: string };
    recipients: ExperienceNotification["recipients"];
    onChange: (recipients: ExperienceNotification["recipients"]) => void;
}

const NotificationRow = ({ config, recipients, onChange }: NotificationRowProps) => {
    const [isOpen, { show, hide }] = useVisibility(false);

    const handleSelect = (value: ExperienceNotification["recipients"]) => {
        onChange(value);
        hide();
    };

    return (
        <div>
            <div className="text-sm font-bold text-gray-darker">{config.label}</div>
            <div className="flex items-center text-sm text-gray-darker">
                <span>{config.description}&nbsp;</span>
                <InlineValuePopover
                    isOpen={isOpen}
                    text={RECIPIENTS_COPY[recipients]}
                    error={false}
                    onClick={show}
                    onClickOutside={hide}
                >
                    <PopoverList.Item name="all attendees" onClickItem={() => handleSelect("travelers")}>
                        all attendees
                    </PopoverList.Item>
                    <PopoverList.Item name="organizer only" onClickItem={() => handleSelect("organizer")}>
                        organizer only
                    </PopoverList.Item>
                </InlineValuePopover>
            </div>
        </div>
    );
};
